import styled, { css } from "styled-components";
import { Size, Variant } from "./types";

export const StyledButton = styled.button<{
	$size: Size;
	$variant: Variant;
	$disabled?: boolean;
	$busy?: boolean;
	$flexible?: boolean;
	$mt?: string;
	$mb?: string;
	$ml?: string;
	$mr?: string;
	$width?: string;
	$maxWidth?: string;
}>`
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
	border: none;
	transition: all 0.3s ease;
	gap: 0.5rem;

	font-family: "Inter", sans-serif;

	${({ $flexible }) =>
		$flexible &&
		css`
			flex: 1;
		`}

	/* Margins and sizing */
  ${({ $mt, $mb, $ml, $mr, $width, $maxWidth }) => css`
		margin-top: ${$mt || "0"};
		margin-bottom: ${$mb || "0"};
		margin-left: ${$ml || "0"};
		margin-right: ${$mr || "0"};
		width: ${$width || "auto"};
		max-$width: ${$maxWidth || "initial"};
	`}

  /* $sizes */
  ${({ $size }) => {
		switch ($size) {
			case "sm":
				return css`
					height: 2rem;
					padding: 0.5rem 1rem;
					border-radius: 8px;
				`;
			case "md":
				return css`
					height: 2.75rem;
					padding: 0.75rem 1.25rem;
					border-radius: 8px;
				`;
			case "lg":
				return css`
					height: 3.5rem;
					padding: 1rem 1.5rem;
					border-radius: 8px;
				`;
			default:
				return "";
		}
	}}

  /* $variants */
  ${({ $variant }) => {
		switch ($variant) {
			case "primary":
				return css`
					background: var(--primary);
					color: var(--on-primary);

					&:hover:enabled {
						background: linear-gradient(
								0deg,
								var(--dark-tint) 0%,
								var(--dark-tint) 100%
							),
							var(--primary);
					}
				`;
			case "primary-outline":
				return css`
					background: transparent;
					color: var(--primary);
					border: 1px solid var(--primary);

					&: $disabled {
						border: none;
					}

					&:hover:enabled {
						background: rgba(
							3,
							255,
							143,
							0.08
						); /* Replace with your primary color's RGB */
					}
				`;
			case "secondary":
				return css`
					background: var(--secondary);
					color: var(--on-secondary);
				`;
			case "tertiary":
				return css`
					background: var(--tertiary);
					color: var(--on-tertiary);
				`;
			case "error":
				return css`
					background: var(--error);
					color: var(--on-surface);

					&:hover:enabled {
						background: var(--on-error);
					}
				`;
			case "destructive":
				return css`
					background: var(--error);
					color: #ffffff;

					&:hover:enabled {
						background: linear-gradient(
								0deg,
								var(--dark-tint) 0%,
								var(--dark-tint) 100%
							),
							var(--error);
					}
				`;
			case "outline":
				return css`
					background: transparent;
					border: 1px solid var(--surface-container-highest);
					color: var(--on-surface-$variant);

					&:hover:enabled {
						background-color: #171a17; /* Replace with a CSS variable if available */
					}
				`;
			case "text":
				return css`
					background: transparent;
					color: var(--text-paragraph);
					padding: 0;
					height: auto;

					&:hover:enabled {
						color: var(--text-paragraph);
					}

					&:active:enabled {
						color: var(--text-paragraph-secondary);
					}
				`;
			default:
				return "";
		}
	}}

  /* $disabled state */
  ${({ $disabled }) =>
		$disabled &&
		css`
			background: linear-gradient(
					0deg,
					var(--surface-container-lowest),
					var(--surface-container-lowest)
				),
				linear-gradient(0deg, var(--dark-tint), var(--dark-tint));
			cursor: not-allowed;
			color: var(--on-surface-$variant);
		`}

  /* $busy state */
  ${({ $busy }) =>
		$busy &&
		css`
			pointer-events: none;
		`}

	&:disabled {
		cursor: not-allowed;
	}
`;
